import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { EnvVar } from "@/env";

import { ApiStreamQuestionRequest, chatApi, useApiClient } from "../../api";
import { QUERY_KEYS } from "../../constants";

export const useStreamingQuestion = () => {
  const apiClient = useApiClient();
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const streamQuestion = useCallback(
    async (
      request: ApiStreamQuestionRequest,
      signal: AbortSignal,
      callbacks: {
        onAgentUpdate: (data: { chatId: string; preSetConstraints?: any }) => void;
        onAgentStep: (data: { message: string; agentName: string }) => void;
        onFinalResult: (result: any) => void;
        onError: (result: any) => void;
      }
    ) => {
      return new Promise(() => {
        const performStreaming = async () => {
          try {
            const tokenResponse = await getAccessTokenSilently({
              cacheMode: "on",
              authorizationParams: {
                audience: EnvVar.auth0Audience,
                scope: "profile email read:current_user",
              },
              detailedResponse: true,
            });

            await chatApi.streamQuestion(
              apiClient,
              request,
              tokenResponse.id_token || "",
              {
                onAgentUpdate: (data) => {
                  queryClient.invalidateQueries({
                    queryKey: QUERY_KEYS.chatHistory(),
                  });
                  callbacks.onAgentUpdate(data);
                },
                onAgentStep: callbacks.onAgentStep,
                onFinalResult: callbacks.onFinalResult,
                onError: callbacks.onError,
              },
              signal
            );
          } catch (error) {
            if ((error as any)?.name !== "AbortError") {
              callbacks.onError(error as any);
            }
          }
        };

        performStreaming();
      });
    },
    [apiClient, getAccessTokenSilently, queryClient]
  );

  return { streamQuestion };
};
